/**
 * DealerPlansPage — the "Advisor Access" landing surface. When a visitor
 * clicks "Advisor Access" from the RV Sales Intelligence section on the
 * homepage, they land here and see the two dealer Stripe offers side by
 * side: Advisor Onboarding & Setup ($499 one-time) and Founding Advisor
 * Account ($2,500/year). Both cards link to `/checkout/{product_id}` which
 * hits the same Stripe checkout flow buyers use.
 *
 * Existing dealers can jump straight to sign-in from the header.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, LogIn, ShieldCheck, Sparkles } from 'lucide-react';
import { DEALER_PRODUCTS } from '../data/products';

const FEATURES = {
  dealer_founding_monthly: [
    'Dealer-branded matching page + custom slug',
    'Live lead pipeline (Kanban + AI briefing)',
    'AI Ranger chat — talking points for every lead',
    'Inventory management + comparison tools',
    'Email Center: templates, sequences, tracking',
    'Sales tracker, war board, and analytics',
    'Up to 5 users per dealership location',
  ],
  dealer_setup: [
    'Account configuration & branding',
    'Inventory upload assistance',
    'Custom quiz + intake form setup',
    '1-on-1 platform training call',
    'Launch checklist walkthrough',
    'Priority support during first week',
  ],
};

const DealerPlansPage = () => {
  return (
    <div className="min-h-screen bg-[#05070d] text-white pt-16" data-testid="dealer-plans-page">
      {/* Global site nav renders the RV Match logo — no local header needed. */}

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-300 text-[11px] font-bold uppercase tracking-[0.25em] mb-6">
          <Sparkles className="w-3.5 h-3.5" />
          Advisor Access
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-5">
          The Future Of <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">RV Sales Intelligence</span>
        </h1>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-4">
          Launch on RV Match AI with the Founding Advisor Account — your branded lead engine, pipeline and AI tools, running year-round. White-glove onboarding is available as an add-on once you're on board.
        </p>
        <Link
          to="/dealer-auth"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 hover:text-cyan-200 transition-colors"
          data-testid="dealer-plans-signin"
        >
          <LogIn className="w-4 h-4" /> Already a dealer? Sign in
        </Link>
      </section>

      {/* Product cards */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-1 max-w-xl mx-auto gap-6" data-testid="dealer-plans-grid">
          {DEALER_PRODUCTS.filter((p) => p.id === 'dealer_founding_monthly').map((p) => {
            const isFounding = p.id === 'dealer_founding_monthly';
            const features = FEATURES[p.id] || [];
            return (
              <div
                key={p.id}
                className={`relative rounded-2xl overflow-hidden border ${
                  isFounding
                    ? 'border-cyan-400/40 bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-transparent'
                    : 'border-white/10 bg-white/[0.02]'
                }`}
                data-testid={`dealer-plan-${p.id}`}
              >
                {isFounding && (
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-400 text-black text-[10px] font-black uppercase tracking-widest z-10">
                    Most Complete
                  </div>
                )}
                {p.image && (
                  <div className="aspect-[16/9] w-full bg-[#05070d] overflow-hidden border-b border-white/5">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <div className="p-6 sm:p-7">
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <h3 className="text-xl sm:text-2xl font-black text-white leading-tight">{p.name}</h3>
                    <div className="text-right shrink-0">
                      <div className="text-2xl font-black text-white">{p.priceLabel}</div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {p.payment === 'recurring-yearly' ? 'Per year' : 'One-time'}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed mb-5">{p.tagline}</p>

                  <ul className="space-y-2 mb-6">
                    {features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-200">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${isFounding ? 'text-cyan-300' : 'text-emerald-300'}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={p.href}
                    className={`inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl font-black text-sm transition-colors ${
                      isFounding
                        ? 'bg-cyan-400 hover:bg-cyan-300 text-black'
                        : 'bg-white hover:bg-slate-100 text-[#05070d]'
                    }`}
                    data-testid={`dealer-plan-cta-${p.id}`}
                  >
                    {isFounding ? 'Start Founding Account' : 'Start Setup'}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Onboarding & Setup — add-on offered after the founding purchase */}
        <div className="max-w-xl mx-auto mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="setup-addon-teaser">
          <div className="flex items-baseline justify-between gap-3 mb-1.5">
            <h3 className="text-base font-black text-white">Advisor Onboarding &amp; Setup</h3>
            <div className="text-right shrink-0">
              <span className="text-lg font-black text-white">$499</span>
              <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">one-time</span>
            </div>
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-300 mb-2">Optional add-on — offered after you start your Founding account</p>
          <p className="text-sm text-slate-300 leading-relaxed">
            White-glove launch assistance: account configuration &amp; branding, inventory upload, custom form setup, a 1-on-1 training call, launch checklist and priority first-week support. You'll be able to add it right after checkout.
          </p>
        </div>

        {/* Reassurance strip */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Secure checkout by Stripe</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Full dashboard access on signup</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Cancel or upgrade anytime</span>
        </div>

        <p className="mt-10 text-center text-sm text-slate-400">
          Not sure which to pick?{' '}
          <a href="mailto:advisor@rvmatchai.app" className="text-cyan-300 hover:text-cyan-200 font-semibold">
            Talk to an advisor
          </a>
          .
        </p>
      </section>
    </div>
  );
};

export default DealerPlansPage;
